import { FieldType, QcOperator, OperatorResult } from '90.quickConnect.Models/enums';
import { CtrlRefOperator, Dependency, AllFieldValueTypes, NumericDesc } from '90.quickConnect.Models/models';

export const evalComputedValue = (
  ctrlRefOperator: CtrlRefOperator,
  dependencies: Dependency[],
  scopeFullPathId: string,
): [AllFieldValueTypes, OperatorResult] => {
  // Scope
  // const parentId = scopeFullPathId.substring(0, scopeFullPathId.lastIndexOf('.'));
  // const currentScopeId = scopeFullPathId.substring(scopeFullPathId.lastIndexOf('.') + 1);

  // Dans le cas où on utilise le pattern avec '/'

  const stringToSearch = ctrlRefOperator.controlId.replace('*', '').replace('/', '');
  const patternToSearchInScope = stringToSearch.substring(stringToSearch.lastIndexOf('.') + 1);

  const patternRelated = dependencies.filter((d) =>
    d.id.toUpperCase().startsWith(patternToSearchInScope.toUpperCase()),
  );

  const LIST_TYPES = new Set<FieldType>([
    FieldType.Combo,
    FieldType.HierarchicalList,
    FieldType.CheckBoxList,
    FieldType.RadioList,
  ]);

  // Compute est ok en numérique uniquement si sa valeur est bien un nombre
  const isNumberLike = (v: any) => {
    if (v === null || v === undefined) return false;
    const n = typeof v === 'number' ? v : Number(String(v).trim().replace(',', '.'));
    return Number.isFinite(n);
  };

  const anyListLike = patternRelated.some((d) => LIST_TYPES.has(d.fieldType));
  const allNumericCompatible = patternRelated.every((d) =>
    d.fieldType === FieldType.Compute
      ? isNumberLike(d.value)
      : d.fieldType === FieldType.Numeric ||
        d.fieldType === FieldType.Digits ||
        d.fieldType === FieldType.Slider ||
        d.fieldType === FieldType.Counter,
  );

  const valueToText = (val: any): string => {
    if (val == null) return '';
    if (Array.isArray(val)) {
      return val
        .map((x) => (x && typeof x === 'object' ? String(x.label ?? x.value ?? '') : String(x ?? '')))
        .filter((s) => s.length > 0)
        .join(',');
    }
    if (typeof val === 'object') return String((val as any).label ?? (val as any).value ?? '');
    return String(val ?? '');
  };

  const valuesToCompute = patternRelated
    .filter(
      (f) =>
        f.fieldType === FieldType.Numeric ||
        f.fieldType === FieldType.Digits ||
        f.fieldType === FieldType.Compute ||
        f.fieldType === FieldType.Slider ||
        f.fieldType === FieldType.Counter,
    )
    .map((f) => f as NumericDesc)
    .map((f) => ({ val: +f.value! as number, type: f.fieldType }))
    .filter((v) => !isNaN(v.val));
  const sum = valuesToCompute.reduce((partialSum, a) => partialSum + a.val!, 0);
  const operatorResult: OperatorResult = valuesToCompute.some(
    (obj: { val: number; type: FieldType }) => obj.type === FieldType.Numeric || !Number.isInteger(obj.val),
  )
    ? OperatorResult.DOUBLE
    : OperatorResult.INT;

  switch (ctrlRefOperator.type) {
    case QcOperator.Sum: {
      // Cas 1: tous numériques-compatibles -> la somme
      if (allNumericCompatible) {
        return valuesToCompute.length > 0 ? [sum, operatorResult] : ['', OperatorResult.STRING];
      }

      // Cas 2: s'il existe au moins une "liste" -> concat par virgule (toutes les valeurs)
      if (anyListLike) {
        const joined = patternRelated
          .map((d) => valueToText(d.value))
          .filter((s) => s.length > 0)
          .join(',');
        return [joined, OperatorResult.STRING];
      }

      // Cas 3: sinon concat directe (texte)
      const glued = patternRelated.map((d) => valueToText(d.value)).join('');
      return [glued, OperatorResult.STRING];
    }

    case QcOperator.Avg:
      return [sum / valuesToCompute.length || 0, OperatorResult.DOUBLE];
    default:
      return [undefined, OperatorResult.ERROR];
  }
};
